<template>
  <dialog-scroll-lock content-class="fill-height" :value="value" width="500" v-bind="$attrs" v-on="$listeners">
    <v-card class="d-flex flex-column fill-height">
      <v-card-title class="flex-grow-0">
        Add GIF / sticker
        <v-spacer />
        <v-btn class="mt-n1 mr-n3" icon @click="$emit('input', false)">
          <v-icon v-html="mdiClose" />
        </v-btn>
      </v-card-title>
      <v-card-subtitle class="flex-grow-0">
        Powered by Tenor
      </v-card-subtitle>
      <div class="flex-grow-0 px-3">
        <div class="d-flex" style="width: 100%;">
          <v-text-field
            outlined dense rounded hide-details clearable label="Search"
            v-model="textStaged"
            @click:clear="() => { textStaged = null; isDirty && search(); }"
            @keydown.enter="() => isDirty && search()"
          />
          <v-btn
            class="ml-4"
            v-bind:class="{ 'primary pulse': isDirty }"
            small fab
            :disabled="!isDirty"
            :loading="searchStatus.pending"
            @click="search()"
          >
            <v-icon v-html="mdiMagnify" />
          </v-btn>
        </div>
        <div class="mt-2 mb-n1 pb-6 mx-n1">
          <v-btn class="ma-1" rounded :color="stickers ? null : 'primary'" @click="stickers = false; search(true);">GIFs</v-btn>
          <v-btn class="ma-1" rounded :color="stickers ? 'primary' : null" @click="stickers = true; search(true);">Stickers</v-btn>
        </div>
      </div>
      <div ref="items-container" class="flex-grow-1 pa-3 overflow-y-auto">
        <div style="column-count: 3; column-gap: 12px;">
          <img
            v-for="item in items" :key="item.id"
            class="mb-3 d-block hover"
            style="width: 100%; max-width: 100%;"
            v-bind:style="{ 'aspect-ratio': getPreviewMedia(item).dims[0] / getPreviewMedia(item).dims[1] }"
            :src="proxy(getPreviewMedia(item).url)"
            @error="global.proxyTenor = true"
            @click="select(item)"
          />
        </div>

        <div v-if="next" class="text-center">
          <v-progress-circular
            v-intersect.quiet="entries => (entries[0] || {}).isIntersecting && search()"
            class="my-3"
            indeterminate
          />
        </div>
      </div>
    </v-card>
  </dialog-scroll-lock>
</template>


<style scoped>
.hover {
  cursor: pointer;
}
.hover:hover {
  filter: drop-shadow(0 7px 8px rgba(0, 0, 0, 0.2)) drop-shadow(0 12px 17px rgba(0, 0, 0, 0.14)) drop-shadow(0 5px 22px rgba(0, 0, 0, 0.12));
}
</style>


<script>
import { LOCALE, COUNTRY_CODE } from '@/utils/intl.js';
import { mdiClose, mdiMagnify } from '@mdi/js';


const TENOR_API_KEY = process.env.OP_DEV_CONFIG['tenor-api-key'];


const CACHE = {};


export default {
  name: 'tenor-dialog',

  props: {
    value: Boolean
  },

  data() {
    return {
      text: null,
      textStaged: null,
      next: null,
      items: [],
      stickers: false,
      searchStatus: this.$track('search')
    };
  },

  computed: {
    isDirty() {
      return (this.textStaged || '') != (this.text || '');
    }
  },

  created() {
    Object.assign(this, { types: [ 'GIF', 'sticker' ], mdiClose, mdiMagnify });
  },

  destroyed() {
    // This probably isn't necessary, but Giphy is resource-heavy and I want to make sure it doesn't stick around
    this.el && this.el.parentNode.removeChild(this.el);
  },

  methods: {
    proxy(url) {
      return this.global.proxyTenor ? url.replace('media.tenor.com', 'gif.ellacard.com') : url;
    },

    getPreviewMedia({ media_formats }) {
      return media_formats.tinywebp_transparent || media_formats.tinygif;
    },

    async search(force) {
      // https://developers.google.com/tenor/guides/endpoints

      const stickers = this.stickers;

      if (this.isDirty || force) {
        this.items = [];
        this.next = null;
        this.$refs['items-container'].scrollTop = 0;
      }

      let endpoint;
      const params = {
        key: TENOR_API_KEY,
        country: COUNTRY_CODE,
        locale: LOCALE.replace('-', '_'),
        media_filter: 'gif,tinygif,webp,webp_transparent,tinywebp_transparent',
        limit: 50
      };
      if (this.textStaged) {
        params.q = this.textStaged;
        endpoint = 'search';
      } else {
        endpoint = 'featured';
      }
      if (stickers)
        params.searchfilter = 'sticker';
      if (this.next)
        params.pos = this.next;

      const url = `https://tenor.googleapis.com/v2/${endpoint}?${new URLSearchParams(params)}`;
      if (!CACHE[url])
        CACHE[url] = await fetch(url).then(r => r.json());
      if (stickers != this.stickers)
        // Type was changed while we were searching
        return;
      const { results, next } = CACHE[url];
      this.items.push(...results);
      this.next = next;
      this.text = this.textStaged;
    },

    select(item) {
      const key = [ 'webp_transparent', 'webp', 'gif' ].find(x => item.media_formats[x]);
      const media = item.media_formats[key];
      const [ w, h ] = media.dims;
      this.$emit('select', { url: media.url, w, h });
      this.$emit('input', false);

      // Report the share event to Tenor asynchronously
      const params = { key: TENOR_API_KEY, id: item.id };
      if (this.text)
        params.q = this.text;
      fetch(`https://tenor.googleapis.com/v2/registershare?${new URLSearchParams(params)}`);
    }
  },

  watch: {
    value: {
      immediate: true,
      handler(value) {
        this.$nextTick(this.search);
      }
    }
  }
}
</script>
